import { computed, toValue } from "vue";
import { format, isValid, parseISO } from "date-fns";
import { ar, es, fr, ht, pt, zhCN } from "date-fns/locale";
import {
  AVAILABLE_TIMES,
  DATE_MODE,
  WEEKDAYS,
} from "#ce/shared/filters/availability/composables/useAvailabilityFilterModel";

// date-fns locales for the Sparkle locales other than English (`shared/lib/locale.js`).
const DATE_LOCALES = Object.freeze({ ar, es, fr, ht, pt, "zh-CN": zhCN });

/**
 * Formats an `available_date` for the activator in the given locale: `"2026-09-20"` →
 * `"September 20"`, or `"septiembre 20"` under `es`. Undefined for a value that is not a date.
 *
 * @param {String} value
 * @param {String} [locale] - A Sparkle locale code; English when omitted or unknown.
 * @returns {String|undefined}
 */
export function humanDate(value, locale) {
  if (typeof value !== "string") {
    return undefined;
  }

  const date = parseISO(value);

  return isValid(date) ? format(date, "MMMM d", { locale: DATE_LOCALES[locale] }) : undefined;
}

// Splits sorted days into runs of consecutive days: [1, 2, 3, 5] → [[1, 2, 3], [5]].
function consecutiveRuns(days) {
  return days.reduce((runs, day) => {
    const run = runs.at(-1);

    if (run && run.at(-1) + 1 === day) {
      run.push(day);
    } else {
      runs.push([day]);
    }

    return runs;
  }, []);
}

/**
 * Summarizes weekday numbers for the activator: `[1, 2, 3, 5]` → `{ text: "M-W, F" }`, one day →
 * `{ text: "Monday", single: true }` (`"Mondays"` when pluralized), none → `{ text: "" }`.
 *
 * @param {Number[]} days
 * @param {(key: String) => String} t
 * @param {Boolean} [pluralize]
 * @returns {{ text: String, single?: Boolean }}
 */
export function summarizeDays(days, t, pluralize = false) {
  const sorted = [...new Set(days)].filter((day) => WEEKDAYS.includes(day)).sort((a, b) => a - b);

  if (sorted.length === 0) {
    return { text: "" };
  }

  if (sorted.length === 1) {
    return { text: t(`${pluralize ? "weekday_plural" : "weekday_long"}_${sorted[0]}`), single: true };
  }

  const short = (day) => t(`weekday_short_${day}`);
  const text = consecutiveRuns(sorted)
    .map((run) => (run.length > 2 ? `${short(run[0])}-${short(run.at(-1))}` : run.map(short).join(", ")))
    .join(", ");

  return { text };
}

/**
 * The activator text for the availability filter: `"In Person: Mornings"`, `"September 20"`,
 * `"Online Booking Only"`. Undefined while filtering is off.
 *
 * @param {Object} sources - Each a ref or getter.
 * @param {import("vue").MaybeRefOrGetter<Boolean>} sources.onlineBookingOnly
 * @param {import("vue").MaybeRefOrGetter<String|undefined>} sources.visitTypeText
 * @param {import("vue").MaybeRefOrGetter<String|undefined>} sources.availableTimes
 * @param {import("vue").MaybeRefOrGetter<String|undefined>} sources.availableDate
 * @param {import("vue").MaybeRefOrGetter<Number[]>} sources.availableDays
 * @param {import("vue").MaybeRefOrGetter<String|undefined>} sources.dateMode
 * @param {(key: String) => String} t
 * @param {Object} [options]
 * @param {import("vue").MaybeRefOrGetter<String>} [options.locale] - The locale dates are formatted in.
 */
export function useAvailabilityFilterSummary(sources, t, { locale } = {}) {
  function timeContext() {
    return toValue(sources.availableTimes) === AVAILABLE_TIMES.morning ? "morning" : "afternoon";
  }

  // A mode picked before its date or days are chosen reads as the time alone.
  function titleForAvailableTime() {
    const context = timeContext();
    const mode = toValue(sources.dateMode);
    const date = humanDate(toValue(sources.availableDate), toValue(locale));
    const days = summarizeDays(toValue(sources.availableDays), t);

    if (mode === DATE_MODE.TODAY) {
      return t(`summary_${context}_today`);
    }
    if (mode === DATE_MODE.SPECIFIC_DAY && date) {
      return `${date}, ${t(`summary_${context}`)}`;
    }
    if (mode === DATE_MODE.DAY_OF_WEEK && days.text) {
      return `${days.text}${days.single ? "" : ","} ${t(`summary_${context}s`)}`;
    }

    return t(`summary_${context}s`);
  }

  function titleForAnyAvailableTime() {
    const mode = toValue(sources.dateMode);

    if (mode === DATE_MODE.TODAY) {
      return t("summary_today");
    }
    if (mode === DATE_MODE.SPECIFIC_DAY) {
      return humanDate(toValue(sources.availableDate), toValue(locale));
    }
    if (mode === DATE_MODE.DAY_OF_WEEK) {
      return summarizeDays(toValue(sources.availableDays), t, true).text;
    }

    return undefined;
  }

  const text = computed(() => {
    if (!toValue(sources.onlineBookingOnly)) {
      return undefined;
    }

    const visitType = toValue(sources.visitTypeText);
    const time = toValue(sources.availableTimes) ? titleForAvailableTime() : titleForAnyAvailableTime();

    if (time) {
      return `${visitType ?? t("summary_base")}: ${time}`;
    }

    return visitType ?? t("summary_only");
  });

  return { text };
}
